<mat-card class="device-list-report">
    <div class="report-header">
        <div>
            <div class="report-eyebrow">REPORTS / DEVICE INVENTORY</div>
            <h4>Device List Report</h4>
            <p class="report-description">Find the devices you need. Build a report with the details that matter.</p>
            <div class="report-count" *ngIf="org">{{org.name}}</div>
        </div>
        <div class="report-summary" aria-label="Report configuration">
            <div><strong>{{customFilters.length}}</strong><span>Filter rules</span></div>
            <div><strong>{{selectedColumns.length}}</strong><span>Columns</span></div>
            <div><strong>{{generateResponse ? getTotalDeviceCount() : '—'}}</strong><span>Matching devices</span></div>
        </div>
    </div>

    <div class="section-heading"><span class="section-number">01</span><div><h5>Choose your scope</h5><p>Select device status and organizations to include.</p></div></div>
    <div class="report-filters">
        <mat-form-field appearance="outline">
            <mat-label>Device status</mat-label>
            <mat-select [(ngModel)]="registration" (selectionChange)="filtersChanged()">
                <mat-option value="all">All (registered and pending)</mat-option>
                <mat-option value="registered">Registered only</mat-option>
                <mat-option value="pending">Pending only</mat-option>
            </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline" *ngIf="currentRole === 0">
            <mat-label>MMSP</mat-label>
            <mat-select [(ngModel)]="selectedMmsp" (selectionChange)="onSelectMMSP($event.value)">
                <div class="dropdown-search" *ngIf="mmspList.length > 1">
                    <input matInput [(ngModel)]="mmspSearchText" [ngModelOptions]="{standalone: true}"
                        (ngModelChange)="filterMmspOptions($event)" placeholder="Search MMSP" autocomplete="off">
                </div>
                <mat-option *ngFor="let organization of filteredMmspList" [value]="organization.orgId">
                    {{organization.name}} <span *ngIf="organization.role >= 0">({{getOrgRoleLabel(organization.role)}})</span>
                </mat-option>
            </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" *ngIf="mspList.length">
            <mat-label>MSP</mat-label>
            <mat-select [(ngModel)]="selectedMsp" (selectionChange)="onSelectMSP($event.value)">
                <div class="dropdown-search" *ngIf="mspList.length > 1">
                    <input matInput [(ngModel)]="mspSearchText" [ngModelOptions]="{standalone: true}"
                        (ngModelChange)="filterMspOptions($event)" placeholder="Search MSP" autocomplete="off">
                </div>
                <mat-option *ngFor="let organization of filteredMspList" [value]="organization.orgId">
                    {{organization.name}} <span *ngIf="organization.role >= 0">({{getOrgRoleLabel(organization.role)}})</span>
                </mat-option>
            </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" *ngIf="orgList.length">
            <mat-label>Organizations</mat-label>
            <mat-select [(ngModel)]="selectedOrg" (selectionChange)="onSelectOrg($event.value)">
                <div class="dropdown-search" *ngIf="orgList.length > 1">
                    <input matInput [(ngModel)]="orgSearchText" [ngModelOptions]="{standalone: true}"
                        (ngModelChange)="filterOrgOptions($event)" placeholder="Search Organization" autocomplete="off">
                </div>
                <mat-option *ngFor="let organization of filteredOrgList" [value]="organization.orgId">
                    {{organization.name}} <span *ngIf="organization.role >= 0">({{getOrgRoleLabel(organization.role)}})</span>
                </mat-option>
            </mat-select>
        </mat-form-field>
    </div>

    <section class="custom-filter-builder" aria-label="Custom device filters">
        <div class="filter-heading">
            <div class="section-heading"><span class="section-number">02</span><div><h5>Refine your results <span class="optional-label">Optional</span></h5><p>Drag a field into the rules, or click to add. Text comparisons ignore case.</p></div></div>
            <button type="button" mat-button (click)="clearFilters()" [disabled]="!customFilters.length">Clear filters</button>
        </div>
        <div class="filter-builder-layout">
            <div class="filter-field-palette">
                <mat-form-field><mat-label>Find a field</mat-label><input matInput [(ngModel)]="filterFieldSearch"></mat-form-field>
                <div class="filter-field-options">
                    <button type="button" *ngFor="let field of getFilterFields()" draggable="true"
                        (dragstart)="startFilterDrag($event, field.path)" (dragend)="endFilterDrag()"
                        (click)="addFilter(field.path)" [attr.aria-label]="'Add filter for ' + field.path">
                        <span><strong>{{field.label}}</strong><small>{{field.path}}</small></span><mat-icon>add</mat-icon>
                    </button>
                </div>
                <div class="custom-path-input">
                    <mat-form-field><mat-label>Custom field path</mat-label><input matInput [(ngModel)]="customFilterPath"
                        placeholder="statistics.firmware.currentVersion" (keyup.enter)="addFilter(customFilterPath)"></mat-form-field>
                    <button type="button" mat-button (click)="addFilter(customFilterPath)" [disabled]="!customFilterPath.trim()">Add</button>
                </div>
            </div>
            <div class="filter-rules" (dragover)="$event.preventDefault()" (drop)="dropFilter($event)">
                <mat-form-field><mat-label>Match devices using</mat-label>
                    <mat-select [(ngModel)]="filterMode" (selectionChange)="filtersChanged()">
                        <mat-option value="all">All rules (AND)</mat-option><mat-option value="any">Any rule (OR)</mat-option>
                    </mat-select>
                </mat-form-field>
                <div class="filter-drop-hint" *ngIf="!customFilters.length"><mat-icon aria-hidden="true">filter_list</mat-icon><strong>Drop a field to get started</strong><span>Or choose a field from the list. Without rules, all devices in your scope are included.</span></div>
                <div class="filter-rule" *ngFor="let rule of customFilters; let i = index"
                    (dragover)="$event.preventDefault()" (drop)="dropFilter($event, i)">
                    <span class="filter-drag-handle" draggable="true" (dragstart)="startFilterDrag($event, rule.path, i)"
                        (dragend)="endFilterDrag()" title="Drag to reorder"><mat-icon>drag_indicator</mat-icon></span>
                    <mat-form-field class="filter-path"><mat-label>Field path</mat-label><input matInput [(ngModel)]="rule.path" (ngModelChange)="filtersChanged()"></mat-form-field>
                    <mat-form-field><mat-label>Comparison</mat-label><mat-select [(ngModel)]="rule.operator" (selectionChange)="filtersChanged()">
                        <mat-option value="eq">Equals</mat-option><mat-option value="ne">Does not equal</mat-option>
                        <mat-option value="contains">Contains</mat-option><mat-option value="startsWith">Starts with</mat-option>
                        <mat-option value="isNull">Is null / missing</mat-option>
                        <mat-option value="isEmpty">Is empty / null / missing</mat-option>
                    </mat-select></mat-form-field>
                    <mat-form-field *ngIf="rule.operator !== 'isNull' && rule.operator !== 'isEmpty'"><mat-label>Value</mat-label><input matInput [(ngModel)]="rule.value" (ngModelChange)="filtersChanged()" placeholder="5.0-b4.0-rc3"></mat-form-field>
                    <div class="filter-rule-actions">
                        <button type="button" mat-icon-button aria-label="Move filter up" [disabled]="i === 0" (click)="moveFilter(i, -1)"><mat-icon>keyboard_arrow_up</mat-icon></button>
                        <button type="button" mat-icon-button aria-label="Move filter down" [disabled]="i === customFilters.length - 1" (click)="moveFilter(i, 1)"><mat-icon>keyboard_arrow_down</mat-icon></button>
                        <button type="button" mat-icon-button aria-label="Remove filter" (click)="removeFilter(i)"><mat-icon>close</mat-icon></button>
                    </div>
                </div>
                <small>Rules apply to Preview, Generate, device counts, and CSV export. CSV device selection is combined with these rules.</small>
            </div>
        </div>
        <div class="request-error" role="alert" *ngIf="filterError">{{filterError}}</div>
    </section>

    <div class="csv-device-import" *ngIf="!loading">
        <div class="csv-device-import-copy">
            <mat-icon aria-hidden="true">upload_file</mat-icon>
            <div>
                <strong>Search devices from CSV</strong>
                <span>Import a headed or headerless CSV containing a MAC address, device name, or both.</span>
            </div>
        </div>
        <div class="csv-device-import-actions">
            <input #deviceCsvInput type="file" accept=".csv,text/csv" hidden
                (change)="onDeviceCsvSelected($event)">
            <button type="button" mat-stroked-button (click)="deviceCsvInput.click()">
                <mat-icon>file_upload</mat-icon>
                Import CSV
            </button>
            <button type="button" mat-button *ngIf="importedDevices.length" (click)="clearImportedDevices()">
                Clear
            </button>
        </div>
        <div class="csv-device-import-status" *ngIf="importedDevices.length">
            <strong>{{importedDevices.length}}</strong> devices imported from {{csvImportFileName}}. Preview is filtered to matching MAC addresses or device names.
        </div>
        <div class="csv-device-import-error" *ngIf="csvImportError">{{csvImportError}}</div>
    </div>

    <mat-spinner *ngIf="loading" [diameter]="30" class="m-auto"></mat-spinner>

    <div class="section-heading" *ngIf="!loading"><span class="section-number">03</span><div><h5>Choose report columns</h5><p>Add fields, then arrange them in the order you want to export.</p></div></div>
    <div class="column-builder" *ngIf="!loading">
        <div class="column-list">
            <div class="column-list-header">
                <h5>Available Columns</h5>
                <span>{{getFilteredAvailableColumns().length}} of {{availableColumns.length}} fields</span>
            </div>
            <div class="column-search">
                <mat-form-field>
                    <mat-label>Search columns</mat-label>
                    <input matInput type="text" [(ngModel)]="availableColumnSearch" autocomplete="off">
                    <button mat-button *ngIf="availableColumnSearch" matSuffix mat-icon-button aria-label="Clear"
                        (click)="clearAvailableColumnSearch()">
                        <mat-icon>close</mat-icon>
                    </button>
                </mat-form-field>
            </div>
            <div class="column-list-body">
                <button type="button" class="column-option" *ngFor="let column of getFilteredAvailableColumns()"
                    [class.selected]="isColumnSelected(column)" (click)="addColumn(column)">
                    <span>
                        <strong>{{column.label}}</strong>
                        <small>{{column.path}}</small>
                    </span>
                    <mat-icon>{{isColumnSelected(column) ? 'check' : 'add'}}</mat-icon>
                </button>
            </div>
        </div>

        <div class="column-list selected-column-list">
            <div class="column-list-header">
                <h5>Selected Columns</h5>
                <span>{{getFilteredSelectedColumns().length}} of {{selectedColumns.length}} selected</span>
            </div>
            <div class="column-search">
                <mat-form-field>
                    <mat-label>Search selected</mat-label>
                    <input matInput type="text" [(ngModel)]="selectedColumnSearch" autocomplete="off">
                    <button mat-button *ngIf="selectedColumnSearch" matSuffix mat-icon-button aria-label="Clear"
                        (click)="clearSelectedColumnSearch()">
                        <mat-icon>close</mat-icon>
                    </button>
                </mat-form-field>
            </div>
            <div class="column-list-body">
                <div class="selected-column-empty" *ngIf="!selectedColumns.length">
                    Select columns from the left.
                </div>
                <div class="selected-column-empty" *ngIf="selectedColumns.length && !getFilteredSelectedColumns().length">
                    No selected columns match your search.
                </div>
                <div class="selected-column-option" *ngFor="let column of getFilteredSelectedColumns()">
                    <div>
                        <strong>{{column.label}}</strong>
                        <small>{{column.path}}</small>
                    </div>
                    <div class="selected-column-actions">
                        <button type="button" mat-icon-button matTooltip="Move up"
                            [disabled]="selectedColumns.indexOf(column) === 0"
                            (click)="moveColumn(column, -1)">
                            <mat-icon>keyboard_arrow_up</mat-icon>
                        </button>
                        <button type="button" mat-icon-button matTooltip="Move down"
                            [disabled]="selectedColumns.indexOf(column) === selectedColumns.length - 1"
                            (click)="moveColumn(column, 1)">
                            <mat-icon>keyboard_arrow_down</mat-icon>
                        </button>
                        <button type="button" mat-icon-button matTooltip="Remove" (click)="removeColumn(column)">
                            <mat-icon>close</mat-icon>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="preview-actions" *ngIf="!loading">
        <div>
            <h5>{{getTableTitle()}}</h5>
            <span>{{tableGenerated ? 'Your report is ready to review and export.' : 'Preview up to 5 matching devices before generating the full report.'}}</span>
        </div>
        <div class="preview-buttons">
            <button type="button" mat-stroked-button [disabled]="generateLoading" (click)="generatePreview()">
                <mat-icon>visibility</mat-icon>
                {{generateLoading && !tableGenerated ? 'Previewing...' : 'Preview'}}
            </button>
            <button type="button" mat-stroked-button
                [disabled]="!canExportCsv || !generatedColumns.length || !getTableRows().length"
                (click)="exportGeneratedTableCsv()">
                <mat-icon>file_download</mat-icon>
                Export CSV
            </button>
            <button type="button" mat-raised-button color="primary" [disabled]="generateLoading"
                (click)="generateRequest()">
                <mat-icon>table_chart</mat-icon>{{generateLoading ? 'Generating...' : 'Generate report'}}
            </button>
        </div>
    </div>

    <div class="request-status request-error" *ngIf="generateError">{{generateError}}</div>
    <div class="request-status request-success" *ngIf="generateResponse && !generateError">
        <mat-icon aria-hidden="true">check_circle</mat-icon> {{getDisplayedDeviceCount()}} of {{getTotalDeviceCount()}} matching devices loaded.
    </div>

    <div class="preview-table" *ngIf="!loading && generatedColumns.length">
        <table mat-table [dataSource]="getTableRows()" class="w-100">
            <ng-container *ngFor="let column of generatedColumns" [matColumnDef]="column.id">
                <th mat-header-cell *matHeaderCellDef>{{column.label}}</th>
                <td mat-cell *matCellDef="let device">
                    <ng-container *ngIf="isTerminalJumpColumn(column); else standardColumnValue">
                        <a *ngIf="getTerminalJumpUrl(device); else emptyTerminalJump"
                            class="table-link" [href]="getTerminalJumpUrl(device)" target="_blank"
                            rel="noopener noreferrer" (click)="trackTerminalJump(device)">
                            Terminal Jump
                        </a>
                        <ng-template #emptyTerminalJump>-</ng-template>
                    </ng-container>
                    <ng-template #standardColumnValue>{{getColumnValue(device, column)}}</ng-template>
                </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="generatedColumnIds; sticky: true"></tr>
            <tr mat-row *matRowDef="let row; columns: generatedColumnIds"></tr>
        </table>
        <div class="preview-empty" *ngIf="!getTableRows().length"><mat-icon aria-hidden="true">search_off</mat-icon><strong>No matching devices</strong><span>Try adjusting your filters or selecting a broader organization scope.</span></div>
    </div>

    <div class="preview-empty" *ngIf="!loading && !generatedColumns.length">
        <mat-icon aria-hidden="true">table_chart</mat-icon><strong>Your report starts here</strong><span>Choose Preview for a quick look, or Generate report for all matching devices.</span>
    </div>
</mat-card>
